import type { CollectionQuery } from './api';
import { cleDeComparaison, foldLigatures } from './foldLigatures';
import { normalizeCuisine } from './normalizeCuisine';
import type { RecipeCourse, RecipeStatus } from './schema';

/**
 * « Où est-ce que ça va se ranger ? » — le prédicat d'une collection, en
 * JavaScript (US-73).
 *
 * ### Pourquoi ce fichier existe
 *
 * Une collection est une **recherche**, pas un dossier (US-21, C6 du plan de
 * vague) : il n'existe aucune table de jointure entre recette et collection, et
 * c'est une décision, pas un oubli. On ne peut donc pas « ranger » une entrée
 * du sas dans une collection — on peut seulement décrire la recette, et le
 * rangement **se calcule**.
 *
 * D'où la propriété qui compte : **le modèle ne voit jamais la liste des
 * collections et n'en choisit aucune.** Au pire il propose une mauvaise
 * cuisine, qu'on voit sur la carte et qu'on retire ; il ne peut pas halluciner
 * un rangement, parce que le rangement est du code.
 *
 * ### Pourquoi il vit dans `shared/` et non sur le serveur
 *
 * La ligne « Rejoindra : *Plats italiens* · *Moins de 30 min* » doit se
 * recalculer **immédiatement** quand on retire une puce, sans aller-retour :
 * une ligne qui clignote une demi-seconde après le geste ne se lit plus comme
 * une conséquence. Elle est donc calculée dans le navigateur — et la seule
 * façon d'éviter que le navigateur et le serveur ne divergent est qu'il n'y ait
 * **qu'une implémentation**, ici.
 *
 * ### Ce que ce prédicat n'est pas
 *
 * Ce n'est pas un second moteur de recherche. `searchRecipes.ts` reste seul à
 * interroger la bibliothèque : lui parle à SQLite, à FTS5 et à la clause de
 * visibilité, sur des dizaines de milliers de lignes. Celui-ci répond à une
 * question beaucoup plus étroite — *cet objet-là, que je tiens déjà en
 * entier, satisfait-il ces critères ?* — pour une poignée de brouillons.
 *
 * Les deux doivent néanmoins **dire la même chose**, et c'est ce qu'un test
 * d'équivalence vérifie sur un même jeu de cas : une ligne « Rejoindra » qui
 * annonce une collection que la recherche ne ramènera pas est pire que pas de
 * ligne du tout.
 */

/**
 * Ce qu'il faut savoir d'une recette — ou d'un brouillon du sas — pour décider
 * si une collection la ramène.
 *
 * **Ce n'est pas une `Recipe`**, et c'est délibéré : une entrée du sas n'en est
 * pas une (elle n'a pas d'identifiant de recette, pas d'ingrédients parsés, pas
 * de ligne dans `recipe_fts`). C'est la forme minimale que les deux savent
 * prendre.
 */
export interface CandidatDeCollection {
  /**
   * Le texte qu'interroge la recherche plein texte : titre, ingrédients,
   * étapes et source, exactement les quatre colonnes que `recipe_fts` porte
   * depuis US-64 (les notes en sont sorties, elles sont devenues personnelles).
   */
  texte: string;
  /**
   * La note **du demandeur**, à part : `searchRecipes` ne l'interroge pas par
   * FTS mais par un `LIKE` sur le texte entier (US-64). Le prédicat reproduit
   * cette différence plutôt que de l'aplatir — sinon il trouverait des recettes
   * que la recherche ne trouve pas.
   */
  note: string | null;
  course: RecipeCourse | null;
  /** Forme affichée ; la comparaison passe par `normalizeCuisine`, comme en SQL. */
  cuisine: string | null;
  /**
   * Les étiquettes que la recette porte — ou **portera** pour un brouillon :
   * les étiquettes calculées que ses temps et ses étapes lui vaudront (US-05,
   * US-61) et les étiquettes manuelles proposées qui n'ont pas été retirées.
   */
  tags: readonly string[];
  /**
   * Le rangement déclaré **par le demandeur** (US-51). `null` pour un
   * brouillon : une entrée du sas n'a pas encore de statut, et une collection
   * qui en exige un ne la ramènera donc pas — ce qui est vrai, et pas seulement
   * prudent.
   */
  status: RecipeStatus | null;
}

/**
 * Découpe un texte en jetons comparables, à la façon du tokenizer
 * `unicode61 remove_diacritics 2` de FTS5 : accents et casse repliés, tout ce
 * qui n'est ni lettre ni chiffre sépare deux jetons.
 */
function jetons(texte: string): string[] {
  return cleDeComparaison(texte)
    .split(/[^\p{L}\p{N}]+/u)
    .filter((jeton) => jeton.length > 0);
}

/**
 * Une « phrase » de la requête — ce que `toPrefixQuery` produit pour un mot
 * séparé par des espaces : `"mot"*`. Les guillemets en font une phrase, donc
 * ses jetons doivent se suivre ; le `*` ne porte que sur le dernier. C'est
 * ainsi que « d'eau » (deux jetons pour FTS5) ne trouve pas un texte où « d »
 * et « eau » sont éloignés.
 */
function phraseTrouvee(texteJetons: readonly string[], phrase: readonly string[]): boolean {
  for (let depart = 0; depart + phrase.length <= texteJetons.length; depart += 1) {
    let complete = true;
    for (let index = 0; index < phrase.length; index += 1) {
      const attendu = phrase[index] ?? '';
      const jeton = texteJetons[depart + index] ?? '';
      // Le dernier jeton est un préfixe (US-04 : les résultats se mettent à
      // jour à la frappe) ; les précédents sont des mots entiers.
      const ok = index === phrase.length - 1 ? jeton.startsWith(attendu) : jeton === attendu;
      if (!ok) { complete = false; break; }
    }
    if (complete) return true;
  }
  return false;
}

/**
 * Le terme de recherche (US-04), tel que `searchRecipes` l'applique : chaque
 * mot tapé doit être trouvé — c'est un ET implicite —, **ou** la requête
 * entière doit apparaître dans la note du demandeur.
 */
function termeTrouve(candidat: CandidatDeCollection, terme: string): boolean {
  const phrases = foldLigatures(terme)
    .trim()
    .split(/\s+/)
    .map((mot) => jetons(mot))
    .filter((phrase) => phrase.length > 0);
  if (phrases.length === 0) return true;

  const texteJetons = jetons(candidat.texte);
  if (phrases.every((phrase) => phraseTrouvee(texteJetons, phrase))) return true;

  // La note du demandeur, par sous-chaîne et non par jeton : c'est ce que fait
  // le `LIKE` de `searchRecipes`, et le redire autrement ferait diverger les
  // deux.
  const note = cleDeComparaison(candidat.note);
  return note !== '' && note.includes(cleDeComparaison(terme));
}

/**
 * Ce candidat satisfait-il les critères de cette collection ?
 *
 * Les critères se **cumulent par ET**, comme dans la barre de filtres : en
 * ajouter un restreint toujours (US-21). Une requête vide ramène tout, ce qui
 * est le comportement d'une collection sans critère.
 */
export function matchesCollectionQuery(candidat: CandidatDeCollection, query: CollectionQuery): boolean {
  if (query.q !== undefined && query.q.trim() !== '' && !termeTrouve(candidat, query.q)) return false;

  if (query.status !== undefined && candidat.status !== query.status) return false;

  if (query.course !== undefined && candidat.course !== query.course) return false;

  const cuisine = normalizeCuisine(query.cuisine);
  if (cuisine !== null && normalizeCuisine(candidat.cuisine) !== cuisine) return false;

  // « Les porte **toutes** » : cocher une seconde étiquette restreint, ce qui
  // est ce à quoi on s'attend en cochant une seconde case.
  const demandees = (query.tags ?? []).filter((nom) => nom.trim().length > 0);
  const portees = new Set(candidat.tags.map((nom) => cleDeComparaison(nom)));
  return demandees.every((nom) => portees.has(cleDeComparaison(nom)));
}
